import { CSSProperties, useCallback, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { AppId, ProductLogo } from "@datassential/platform-ui-lib";
import { addFileToPackage } from "@actions/packages.actions";
import { useMacDetails } from "@apiHooks/useMacDetails";
import { useMacExport } from "@apiHooks/useMacExport";
import { routeNames } from "@core/routeNames";
import useElementHeight from "@core/hooks/useElementHeight";
import { IMacKeyword, MacStageKey } from "@models/MacDetails";
import { toggleInArray } from "@helpers";
import MacChart from "./MacChart/MacChart";
import MacDetailsActions from "./MacDetailsActions/MacDetailsActions";
import MacDetailsHeader from "./MacDetailsHeader/MacDetailsHeader";
import MacMenuExamples from "./MacMenuExamples/MacMenuExamples";
import "./MacDetails.scss";

function getMacIdFromSearch(search: string): number {
    return Number(new URLSearchParams(search).get('id')) || 0;
}

function MacDetails() {
    const dispatch = useDispatch();
    const location = useLocation();
    const macId = getMacIdFromSearch(location.search);
    const { data: mac, isLoading } = useMacDetails(macId);
    const { downloadingIds, exportMac } = useMacExport();
    const [selectedStages, setSelectedStages] = useState<MacStageKey[]>([]);
    const [selectedKeyword, setSelectedKeyword] = useState<IMacKeyword | null>(null);
    // The chart fits the screen height left by the header and the actions.
    const { ref: headerRef, height: headerHeight } = useElementHeight<HTMLDivElement>();
    const { ref: actionsRef, height: actionsHeight } = useElementHeight<HTMLDivElement>();

    const toggleStage = useCallback((stage: MacStageKey) => {
        setSelectedStages((stages) => toggleInArray(stages, stage));
    }, []);

    const addToPackage = useCallback(() => {
        toast.success('Item has been added to package');
        dispatch(addFileToPackage({ itemId: macId, itemDocumentType: 'mac' }));
    }, [dispatch, macId]);

    const sendToKeywords = (mac?.stages ?? [])
        .filter((stage) => selectedStages.includes(stage.key))
        .flatMap((stage) => stage.keywords.map((keyword) => keyword.text));

    // getMacDetails resolves null for any failed load (non-2xx status, network error, empty body).
    if (!isLoading && !mac) {
        return <Redirect to={routeNames.error}/>;
    }

    return (
        <div className="MacDetails">
            <div
                className="MacDetails__content"
                style={{
                    '--mac-details-header-height': `${headerHeight}px`,
                    '--mac-details-actions-height': `${actionsHeight}px`,
                } as CSSProperties}
            >
                <div className="MacDetails__header" ref={headerRef}>
                    <MacDetailsHeader/>
                </div>
                {isLoading && (
                    <div className="MacDetails__spinner">
                        <ProductLogo appId={AppId.Snap} type="regular"/>
                    </div>
                )}
                {!isLoading && mac && (
                    <>
                        <MacChart
                            mac={mac}
                            selectedStages={selectedStages}
                            selectedKeywordId={selectedKeyword?.id ?? null}
                            onToggleStage={toggleStage}
                            onViewMenuExamples={setSelectedKeyword}
                        />
                        <div className="MacDetails__actions" ref={actionsRef}>
                            <MacDetailsActions
                                sendToKeywords={sendToKeywords}
                                canSendTo={!mac.isNonUs}
                                isDownloading={downloadingIds.includes(mac.id)}
                                onAddToPackage={addToPackage}
                                onExport={(format) => exportMac(mac.id, format)}
                            />
                        </div>
                        {selectedKeyword && (
                            // Keyed by keyword so the search term resets when another keyword is chosen.
                            <MacMenuExamples
                                key={selectedKeyword.id}
                                macId={mac.id}
                                countryCode={mac.countryCode}
                                keyword={selectedKeyword}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default MacDetails;
